import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Shield, FileText, Users, MessageSquare, Check, X, Edit, Trash2, RefreshCw, Lock, Image, Save, BookOpen, Heart, Plus, ChevronUp, ChevronDown, Star, Award, Zap, Upload, Download, FileSpreadsheet, Phone, Calendar, MapPin, Power } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'venues', name: 'Venues' }, { id: 'photographers', name: 'Photographers' }, { id: 'makeup', name: 'Bridal Makeup' },
  { id: 'mehndi', name: 'Mehndi Artists' }, { id: 'decorators', name: 'Decorators' }, { id: 'dj', name: 'DJs & Music' },
  { id: 'catering', name: 'Caterers' }, { id: 'bridal-wear', name: 'Bridal Wear' }, { id: 'invitations', name: 'Invitations' },
  { id: 'entertainment', name: 'Entertainment & Arts' },
];

const VENDOR_TAGS = ["Best Seller", "Customer's Choice", "Top Rated", "New", "Premium", "Budget Friendly"];

const CITIES = [
  "Toronto", "Montreal", "Vancouver", "Calgary", "Edmonton", "Ottawa",
  "Winnipeg", "Mississauga", "Brampton", "Hamilton", "Surrey",
  "Quebec City", "Halifax", "London", "Markham"
];

const tagColors = {
  "Best Seller": "bg-amber-500 text-white",
  "Customer's Choice": "bg-pink-500 text-white",
  "Top Rated": "bg-yellow-500 text-white",
  "New": "bg-blue-500 text-white",
  "Premium": "bg-purple-500 text-white",
  "Budget Friendly": "bg-green-500 text-white",
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-700';
    case 'approved': return 'bg-green-100 text-green-700';
    default: return 'bg-red-100 text-red-700';
  }
};

const getLeadStatusBadgeClass = (status) => {
  switch (status) {
    case 'new': return 'bg-blue-100 text-blue-700';
    case 'contacted': return 'bg-yellow-100 text-yellow-700';
    case 'converted': return 'bg-green-100 text-green-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const AdminPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [adminKey, setAdminKey] = useState('');
  const [activeTab, setActiveTab] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [realWeddings, setRealWeddings] = useState([]);
  const [shaadiGenieLeads, setShaadiGenieLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [statusFilter, setStatusFilter] = useState('pending');
  
  // Blog form
  const [blogForm, setBlogForm] = useState({ title: '', excerpt: '', content: '', image: '', category: 'Planning' });
  const [showBlogForm, setShowBlogForm] = useState(false);
  
  // Real Wedding form
  const [weddingForm, setWeddingForm] = useState({ title: '', couple_names: '', location: '', wedding_date: '', description: '', images: '' });
  const [showWeddingForm, setShowWeddingForm] = useState(false);
  
  // Vendor category filter
  const [vendorCategoryFilter, setVendorCategoryFilter] = useState('all');
  const [syncing, setSyncing] = useState(false);
  
  // Bulk upload
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkData, setBulkData] = useState('');
  const [bulkUploading, setBulkUploading] = useState(false);

  useEffect(() => {
    const key = searchParams.get('key');
    if (key) { setAdminKey(key); setIsAuthenticated(true); }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated && adminKey) fetchData();
  }, [isAuthenticated, adminKey, activeTab, statusFilter]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'planmyshaadi-admin-2024') {
      setAdminKey(password); setIsAuthenticated(true); toast.success('Welcome to Admin Dashboard');
    } else { toast.error('Invalid password'); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'applications') {
        const res = await axios.get(`${API}/admin/applications?admin_key=${adminKey}&status=${statusFilter}`);
        setApplications(res.data);
      } else if (activeTab === 'vendors') {
        const res = await axios.get(`${API}/admin/vendors?admin_key=${adminKey}`);
        setVendors(res.data);
      } else if (activeTab === 'inquiries') {
        const res = await axios.get(`${API}/admin/inquiries?admin_key=${adminKey}`);
        setInquiries(res.data);
      } else if (activeTab === 'blogs') {
        const res = await axios.get(`${API}/admin/blogs?admin_key=${adminKey}`);
        setBlogs(res.data);
      } else if (activeTab === 'weddings') {
        const res = await axios.get(`${API}/admin/real-weddings?admin_key=${adminKey}`);
        setRealWeddings(res.data);
      } else if (activeTab === 'leads') {
        const res = await axios.get(`${API}/admin/shaadi-genie-leads?admin_key=${adminKey}`);
        setShaadiGenieLeads(res.data);
      }
    } catch (error) { toast.error('Failed to load admin data'); }
    finally { setLoading(false); }
  };

  const handleApprove = async (appId) => {
    try {
      await axios.put(`${API}/admin/applications/${appId}/approve?admin_key=${adminKey}`);
      toast.success('Vendor approved!'); fetchData(); setSelectedApp(null);
    } catch (error) { toast.error('Failed to approve'); }
  };

  const handleReject = async (appId) => {
    try {
      await axios.put(`${API}/admin/applications/${appId}/reject?admin_key=${adminKey}`);
      toast.success('Application rejected'); fetchData(); setSelectedApp(null);
    } catch (error) { toast.error('Failed to reject'); }
  };

  const handleUpdateApplication = async () => {
    try {
      await axios.put(`${API}/admin/applications/${selectedApp.id}?admin_key=${adminKey}`, editData);
      toast.success('Updated'); fetchData(); setEditMode(false); setSelectedApp({ ...selectedApp, ...editData });
    } catch (error) { toast.error('Failed to update'); }
  };

  const handleUpdateVendor = async () => {
    try {
      // Map contact_email to email for vendor update
      const updatePayload = {
        ...editData,
        email: editData.contact_email  // Backend uses 'email' field for vendors
      };
      delete updatePayload.contact_email;
      
      await axios.put(`${API}/admin/vendors/${selectedVendor.id}?admin_key=${adminKey}`, updatePayload);
      toast.success('Vendor updated'); fetchData(); setEditMode(false); setSelectedVendor({ ...selectedVendor, ...editData, email: editData.contact_email });
    } catch (error) { toast.error('Failed to update'); }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await axios.delete(`${API}/admin/vendors/${vendorId}?admin_key=${adminKey}`);
      toast.success('Deleted'); fetchData(); setSelectedVendor(null);
    } catch (error) { toast.error('Failed to delete'); }
  };

  const handleUpdateInquiryStatus = async (inquiryId, status) => {
    try {
      await axios.put(`${API}/admin/inquiries/${inquiryId}/status?admin_key=${adminKey}&status=${status}`);
      toast.success('Status updated'); fetchData();
    } catch (error) { toast.error('Failed'); }
  };

  // Blog functions
  const handleCreateBlog = async () => {
    try {
      await axios.post(`${API}/admin/blogs?admin_key=${adminKey}`, blogForm);
      toast.success('Blog posted!'); fetchData(); setShowBlogForm(false);
      setBlogForm({ title: '', excerpt: '', content: '', image: '', category: 'Planning' });
    } catch (error) { toast.error('Failed to post blog'); }
  };

  const handleDeleteBlog = async (blogId) => {
    if (!window.confirm('Delete this blog?')) return;
    try {
      await axios.delete(`${API}/admin/blogs/${blogId}?admin_key=${adminKey}`);
      toast.success('Blog deleted'); fetchData();
    } catch (error) { toast.error('Failed'); }
  };

  // Real Wedding functions
  const handleCreateWedding = async () => {
    const data = { ...weddingForm, images: weddingForm.images.split(',').map(s => s.trim()).filter(Boolean) };
    try {
      await axios.post(`${API}/admin/real-weddings?admin_key=${adminKey}`, data);
      toast.success('Wedding added!'); fetchData(); setShowWeddingForm(false);
      setWeddingForm({ title: '', couple_names: '', location: '', wedding_date: '', description: '', images: '' });
    } catch (error) { toast.error('Failed to add wedding'); }
  };

  const handleDeleteWedding = async (weddingId) => {
    if (!window.confirm('Delete this wedding?')) return;
    try {
      await axios.delete(`${API}/admin/real-weddings/${weddingId}?admin_key=${adminKey}`);
      toast.success('Deleted'); fetchData();
    } catch (error) { toast.error('Failed'); }
  };

  // Vendor reordering
  const handleMoveVendor = async (vendorId, direction) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/move?admin_key=${adminKey}&direction=${direction}`);
      toast.success('Order updated'); fetchData();
    } catch (error) { toast.error('Failed to reorder'); }
  };

  // Toggle featured status
  const handleToggleFeatured = async (vendorId) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/toggle-featured?admin_key=${adminKey}`);
      toast.success('Featured status updated'); fetchData();
    } catch (error) { toast.error('Failed to update'); }
  };

  // Update vendor tags
  const handleUpdateTags = async (vendorId, tags) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/tags?admin_key=${adminKey}`, tags);
      toast.success('Tags updated'); fetchData();
    } catch (error) { toast.error('Failed to update tags'); }
  };

  // Toggle vendor active (online/offline)
  const handleToggleActive = async (vendorId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}?admin_key=${adminKey}`, { is_active: !currentStatus });
      toast.success(currentStatus ? 'Vendor is now offline' : 'Vendor is now online'); fetchData();
    } catch (error) { toast.error('Failed to update status'); }
  };

  // Update Shaadi Genie lead status
  const handleUpdateLeadStatus = async (leadId, status) => {
    try {
      await axios.put(`${API}/admin/shaadi-genie-leads/${leadId}/status?admin_key=${adminKey}&status=${status}`);
      toast.success('Lead status updated'); fetchData();
    } catch (error) { toast.error('Failed to update'); }
  };

  // Delete Shaadi Genie lead
  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await axios.delete(`${API}/admin/shaadi-genie-leads/${leadId}?admin_key=${adminKey}`);
      toast.success('Lead deleted'); fetchData();
    } catch (error) { toast.error('Failed to delete'); }
  };

  // Move approved vendor to rejected
  const handleMoveToRejected = async (vendorId) => {
    if (!window.confirm('Move this vendor back to rejected list? This will remove them from live vendors.')) return;
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/reject?admin_key=${adminKey}`);
      toast.success('Vendor moved to rejected'); fetchData(); setSelectedVendor(null);
    } catch (error) { toast.error('Failed to move vendor'); }
  };

  // Sync missing vendors
  const handleSyncVendors = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/sync-vendors?admin_key=${adminKey}`);
      toast.success(res.data.message); fetchData();
    } catch (error) { toast.error('Failed to sync'); }
    finally { setSyncing(false); }
  };

  // Normalize vendor order
  const handleNormalizeOrder = async () => {
    try {
      const res = await axios.post(`${API}/admin/vendors/normalize-order?admin_key=${adminKey}`);
      toast.success(res.data.message); fetchData();
    } catch (error) { toast.error('Failed to normalize order'); }
  };

  // Bulk upload vendors
  const handleBulkUpload = async () => {
    setBulkUploading(true);
    try {
      // Parse CSV data
      const lines = bulkData.trim().split('\n');
      if (lines.length < 2) {
        toast.error('Please provide CSV data with header row and at least one vendor');
        return;
      }
      
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      const vendors = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length === 0) continue;
        
        const vendor = {};
        headers.forEach((header, idx) => {
          vendor[header] = values[idx]?.trim() || '';
        });
        
        // Map CSV columns to API fields
        vendors.push({
          business_name: vendor.business_name || vendor.name || '',
          category: vendor.category || 'venues',
          description: vendor.description || '',
          services: vendor.services || '',
          price_range_min: parseInt(vendor.price_range_min || vendor.min_price || '0') || 0,
          price_range_max: parseInt(vendor.price_range_max || vendor.max_price || '0') || 0,
          city: vendor.city || 'Montreal',
          address: vendor.address || '',
          phone: vendor.phone || '',
          email: vendor.email || '',
          website: vendor.website || '',
          social_media: vendor.social_media || vendor.instagram || '',
          images: vendor.images || vendor.image_urls || '',
          is_featured: vendor.is_featured === 'true' || vendor.featured === 'true',
          tags: vendor.tags || ''
        });
      }
      
      if (vendors.length === 0) {
        toast.error('No valid vendors found in CSV');
        return;
      }
      
      const res = await axios.post(`${API}/admin/vendors/bulk?admin_key=${adminKey}`, { vendors });
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} vendor(s) had errors during upload`);
      }
      fetchData();
      setShowBulkUpload(false);
      setBulkData('');
    } catch (error) {
      toast.error('Failed to upload vendors');
    } finally {
      setBulkUploading(false);
    }
  };

  // Parse CSV line handling quoted values
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  // Export vendors as CSV
  const handleExportVendors = async () => {
    try {
      const res = await axios.get(`${API}/admin/vendors/export?admin_key=${adminKey}`);
      const vendors = res.data.vendors;
      
      if (vendors.length === 0) {
        toast.error('No vendors to export');
        return;
      }
      
      // Create CSV content
      const headers = ['business_name', 'category', 'description', 'services', 'price_range_min', 'price_range_max', 'city', 'address', 'phone', 'email', 'website', 'social_media', 'images', 'is_featured', 'tags'];
      const csvContent = [
        headers.join(','),
        ...vendors.map(v => headers.map(h => {
          const val = v[h] || '';
          // Escape quotes and wrap in quotes if contains comma
          if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
            return `"${val.replace(/"/g, '""')}"`;
          }
          return val;
        }).join(','))
      ].join('\n');
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendors_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${vendors.length} vendors`);
    } catch (error) {
      toast.error('Failed to export vendors');
    }
  };

  // Filter vendors by category
  const filteredVendors = useMemo(() => 
    vendorCategoryFilter === 'all' 
      ? vendors 
      : vendors.filter(v => v.category === vendorCategoryFilter),
    [vendors, vendorCategoryFilter]
  );
  
  // Sort vendors: featured first, then by display_order
  const sortedVendors = useMemo(() => 
    [...filteredVendors].sort((a, b) => {
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
      return (a.display_order || 0) - (b.display_order || 0);
    }),
    [filteredVendors]
  );

  const featuredVendors = useMemo(() => 
    sortedVendors.filter(v => v.is_featured),
    [sortedVendors]
  );

  const featuredCount = featuredVendors.length;

  const openEditMode = (item, type) => {
    setEditMode(true);
    setEditData({
      business_name: item.business_name, category: item.category, description: item.description,
      services: item.services || [], price_range_min: item.price_range_min || 0, price_range_max: item.price_range_max || 0,
      city: item.city || 'Montreal', cities: item.cities || [item.city || 'Montreal'], address: item.address || '', phone: item.phone || '',
      contact_email: type === 'application' ? item.contact_email : item.email,
      website: item.website || '', social_media: item.social_media || '', images: item.images || [],
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-8 shadow-lg border">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-maroon-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-maroon-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-gray-500 mt-2">Enter admin password</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input type="password" placeholder="Admin Password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 h-12 rounded-xl" data-testid="admin-password-input" />
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="admin-login-btn">Access Dashboard</Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-maroon-100 rounded-xl flex items-center justify-center"><Shield className="w-5 h-5 text-maroon-600" /></div>
              <h1 className="font-heading font-bold text-lg">Admin Dashboard</h1>
            </div>
            <Button variant="ghost" onClick={() => navigate('/')}>Back to Site</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { id: 'applications', label: 'Applications', icon: FileText },
            { id: 'vendors', label: 'Vendors', icon: Users },
            { id: 'inquiries', label: 'Inquiries', icon: MessageSquare },
            { id: 'leads', label: 'Shaadi Genie Leads', icon: Phone },
            { id: 'blogs', label: 'Blogs', icon: BookOpen },
            { id: 'weddings', label: 'Real Weddings', icon: Heart },
          ].map((tab) => (
            <Button key={tab.id} variant={activeTab === tab.id ? 'default' : 'outline'} onClick={() => setActiveTab(tab.id)} className={`rounded-full ${activeTab === tab.id ? 'bg-maroon-600 text-white' : ''}`} data-testid={`admin-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4 mr-2" />{tab.label}
            </Button>
          ))}
          <Button variant="outline" onClick={fetchData} className="rounded-full ml-auto"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="space-y-4">
            <div className="flex gap-2 mb-4">
              {['pending', 'approved', 'rejected'].map((status) => (
                <Button key={status} variant={statusFilter === status ? 'default' : 'ghost'} size="sm" onClick={() => setStatusFilter(status)} className={`rounded-full capitalize ${statusFilter === status ? 'bg-maroon-500 text-white' : ''}`}>{status}</Button>
              ))}
            </div>
            {applications.length === 0 ? <div className="text-center py-12 text-gray-500">No {statusFilter} applications</div> : (
              <div className="grid gap-4">
                {applications.map((app) => (
                  <div key={app.id} className="bg-white rounded-xl p-6 border hover:border-maroon-300 cursor-pointer" onClick={() => { setSelectedApp(app); setEditMode(false); }}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">{app.business_name}</h3>
                        <p className="text-gray-500 text-sm capitalize">{app.category?.replace('-', ' ')} • {app.cities?.length > 1 ? app.cities.join(', ') : app.city}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {app.special_promotion && (
                          <Badge className="bg-gold-100 text-gold-800 border border-gold-300">$99 Promo</Badge>
                        )}
                        <Badge className={getStatusBadgeClass(app.status)}>{app.status}</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Vendors Tab */}
        {activeTab === 'vendors' && (
          <div className="space-y-4">
            {/* Filters and Actions */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Select value={vendorCategoryFilter} onValueChange={setVendorCategoryFilter}>
                <SelectTrigger className="w-40"><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={handleSyncVendors} disabled={syncing} className="rounded-full" data-testid="sync-vendors-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                Sync Missing
              </Button>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" onClick={handleExportVendors} className="rounded-full" data-testid="export-vendors-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button onClick={() => setShowBulkUpload(true)} className="btn-primary rounded-full" data-testid="bulk-upload-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Bulk Upload
                </Button>
              </div>
            </div>

            {/* Featured Vendors Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" /> Featured Vendors ({featuredCount} vendors)
              </h3>
              <p className="text-sm text-gray-500 mb-3">Drag order using arrows. Featured vendors appear on the homepage in this order.</p>
              
              <div className="space-y-2">
                {featuredVendors.map((vendor, index) => (
                  <div key={vendor.id} className="bg-white rounded-xl p-4 border border-amber-200 flex items-center gap-4" data-testid={`featured-vendor-${vendor.id}`}>
                    {/* Order Controls */}
                    <div className="flex flex-col gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleMoveVendor(vendor.id, 'up'); }}
                        disabled={index === 0}
                        className="h-6 w-6 p-0"
                        data-testid={`move-up-${vendor.id}`}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <span className="text-xs text-center text-gray-400">#{(vendor.display_order || 0) + 1}</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleMoveVendor(vendor.id, 'down'); }}
                        className="h-6 w-6 p-0"
                        data-testid={`move-down-${vendor.id}`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Vendor Info */}
                    <div className="flex-1 flex items-center gap-3 cursor-pointer" onClick={() => { setSelectedVendor(vendor); setEditMode(false); }}>
                      {vendor.images?.[0] && <img src={vendor.images[0]} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                      <div>
                        <h4 className="font-medium">{vendor.business_name}</h4>
                        <p className="text-gray-500 text-xs capitalize">{vendor.category?.replace('-', ' ')}</p>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1">
                      {(vendor.tags || []).map((tag) => (
                        <Badge key={tag} className={`${tagColors[tag] || 'bg-gray-500'} text-xs`}>{tag}</Badge>
                      ))}
                    </div>

                    {/* Quick Actions */}
                    <div className="flex items-center gap-2">
                      <Select 
                        value="" 
                        onValueChange={(tag) => {
                          const currentTags = vendor.tags || [];
                          if (!currentTags.includes(tag)) {
                            handleUpdateTags(vendor.id, [...currentTags, tag]);
                          }
                        }}
                      >
                        <SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="+ Add Tag" /></SelectTrigger>
                        <SelectContent>
                          {VENDOR_TAGS.filter(t => !(vendor.tags || []).includes(t)).map((tag) => (
                            <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleToggleFeatured(vendor.id); }}
                        className="text-amber-500"
                        data-testid={`unfeature-${vendor.id}`}
                      >
                        <Star className="w-4 h-4 fill-current" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Non-Featured Vendors */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Other Vendors</h3>
              <div className="grid gap-3">
                {sortedVendors.filter(v => !v.is_featured).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No non-featured vendors in this category</div>
                ) : (
                  sortedVendors.filter(v => !v.is_featured).map((vendor) => (
                    <div key={vendor.id} className={`bg-white rounded-xl p-4 border hover:border-maroon-300 cursor-pointer flex items-center gap-4 ${!vendor.is_active ? 'opacity-60' : ''}`} onClick={() => { setSelectedVendor(vendor); setEditMode(false); }}>
                      {vendor.images?.[0] && <img src={vendor.images[0]} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                      <div className="flex-1">
                        <h4 className="font-medium">{vendor.business_name}</h4>
                        <p className="text-gray-500 text-xs capitalize">{vendor.category?.replace('-', ' ')} • {vendor.cities?.length > 1 ? vendor.cities.join(', ') : vendor.city}</p>
                      </div>
                      
                      {/* Tags */}
                      <div className="flex flex-wrap gap-1">
                        {(vendor.tags || []).map((tag) => (
                          <Badge key={tag} className={`${tagColors[tag] || 'bg-gray-500'} text-xs`}>{tag}</Badge>
                        ))}
                      </div>

                      {/* Online/Offline Toggle */}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleToggleActive(vendor.id, vendor.is_active); }}
                        className={vendor.is_active ? 'text-green-600' : 'text-gray-400'}
                        title={vendor.is_active ? 'Click to set offline' : 'Click to set online'}
                      >
                        <Power className="w-4 h-4" />
                      </Button>

                      <Badge className={vendor.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{vendor.is_active ? 'Online' : 'Offline'}</Badge>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleToggleFeatured(vendor.id); }}
                        className="text-gray-500 hover:text-amber-500"
                        data-testid={`feature-${vendor.id}`}
                      >
                        <Star className="w-4 h-4 mr-1" /> Feature
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <div className="grid gap-4">
            {inquiries.length === 0 ? <div className="text-center py-12 text-gray-500">No inquiries yet</div> : inquiries.map((inquiry) => (
              <div key={inquiry.id} className="bg-white rounded-xl p-6 border">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold">{inquiry.client_name}</h3>
                    <p className="text-gray-500 text-sm">{inquiry.client_email}</p>
                    {inquiry.client_phone && <p className="text-gray-500 text-sm">📞 {inquiry.client_phone}</p>}
                  </div>
                  <Badge className="bg-maroon-100 text-maroon-700">{inquiry.vendor_name}</Badge>
                </div>
                <p className="text-gray-600 mb-4">{inquiry.message}</p>
                <div className="flex items-center gap-4">
                  <Select value={inquiry.status || 'new'} onValueChange={(v) => handleUpdateInquiryStatus(inquiry.id, v)}>
                    <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <a href={`mailto:${inquiry.client_email}?subject=RE: ${inquiry.vendor_name}`} className="text-sm text-maroon-600 hover:underline">Reply via Email</a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Shaadi Genie Leads Tab */}
        {activeTab === 'leads' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-gold-100 to-gold-50 rounded-xl p-4 border border-gold-200">
              <h3 className="font-semibold text-maroon-800 flex items-center gap-2">
                <Phone className="w-5 h-5" /> Shaadi Genie Leads
              </h3>
              <p className="text-sm text-maroon-600">People looking for wedding vendor recommendations</p>
            </div>
            
            {shaadiGenieLeads.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No leads yet</div>
            ) : (
              <div className="grid gap-4">
                {shaadiGenieLeads.map((lead) => (
                  <div key={lead.id} className="bg-white rounded-xl p-6 border hover:border-gold-300 transition-colors">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-lg">{lead.name}</h3>
                        <p className="text-gray-600 flex items-center gap-2">
                          <Phone className="w-4 h-4" /> {lead.phone}
                        </p>
                        {lead.email && <p className="text-gray-500 text-sm">{lead.email}</p>}
                      </div>
                      <Badge className={getLeadStatusBadgeClass(lead.status)}>
                        {lead.status}
                      </Badge>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-3 mb-4 text-sm">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span>{lead.location || 'Not specified'}{lead.city ? `, ${lead.city}` : ''}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span>{lead.event_date || 'Date not specified'}</span>
                      </div>
                    </div>
                    
                    {lead.vendor_types?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 mb-1">Looking for:</p>
                        <div className="flex flex-wrap gap-1">
                          {lead.vendor_types.map((type, i) => (
                            <Badge key={`vtype-${type}-${i}`} variant="secondary" className="text-xs capitalize">{type.replace('-', ' ')}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {lead.message && (
                      <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg mb-4">{lead.message}</p>
                    )}
                    
                    <div className="flex items-center gap-3 pt-3 border-t">
                      <Select value={lead.status} onValueChange={(v) => handleUpdateLeadStatus(lead.id, v)}>
                        <SelectTrigger className="w-36 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      <a href={`tel:${lead.phone}`} className="text-sm text-maroon-600 hover:underline flex items-center gap-1">
                        <Phone className="w-3 h-3" /> Call
                      </a>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteLead(lead.id)} className="text-red-500 ml-auto">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Blogs Tab */}
        {activeTab === 'blogs' && (
          <div className="space-y-4">
            <Button onClick={() => setShowBlogForm(true)} className="btn-primary"><Plus className="w-4 h-4 mr-2" />New Blog Post</Button>
            {blogs.length === 0 ? <div className="text-center py-12 text-gray-500">No blog posts yet</div> : (
              <div className="grid gap-4">
                {blogs.map((blog) => (
                  <div key={blog.id} className="bg-white rounded-xl p-6 border">
                    <div className="flex gap-4">
                      {blog.image && <img src={blog.image} alt="" className="w-24 h-24 rounded-lg object-cover" />}
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{blog.title}</h3>
                        <p className="text-gray-500 text-sm mb-2">{blog.category} • {new Date(blog.created_at).toLocaleDateString()}</p>
                        <p className="text-gray-600 text-sm line-clamp-2">{blog.excerpt}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteBlog(blog.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Real Weddings Tab */}
        {activeTab === 'weddings' && (
          <div className="space-y-4">
            <Button onClick={() => setShowWeddingForm(true)} className="btn-primary"><Plus className="w-4 h-4 mr-2" />Add Real Wedding</Button>
            {realWeddings.length === 0 ? <div className="text-center py-12 text-gray-500">No real weddings yet</div> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {realWeddings.map((wedding) => (
                  <div key={wedding.id} className="bg-white rounded-xl overflow-hidden border">
                    {wedding.images?.[0] && <img src={wedding.images[0]} alt="" className="w-full h-40 object-cover" />}
                    <div className="p-4">
                      <h3 className="font-semibold">{wedding.title}</h3>
                      <p className="text-gray-500 text-sm">{wedding.couple_names}</p>
                      <p className="text-gray-400 text-xs">{wedding.location} • {wedding.wedding_date}</p>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteWedding(wedding.id)} className="text-red-500 mt-2"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Application Detail Modal */}
      <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Application' : 'Application Details'}</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-4 pt-4">
              {editMode ? (
                <div className="space-y-4">
                  <div><label className="text-sm font-medium mb-1 block">Business Name</label><Input value={editData.business_name} onChange={(e) => setEditData({ ...editData, business_name: e.target.value })} /></div>
                  <div><label className="text-sm font-medium mb-1 block">Category</label>
                    <Select value={editData.category} onValueChange={(v) => setEditData({ ...editData, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{categories.map((cat) => (<SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div><label className="text-sm font-medium mb-1 block">Description</label><Textarea value={editData.description} onChange={(e) => setEditData({ ...editData, description: e.target.value })} rows={4} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="text-sm font-medium mb-1 block">Min Price ($)</label><Input type="number" value={editData.price_range_min} onChange={(e) => setEditData({ ...editData, price_range_min: parseInt(e.target.value) || 0 })} /></div>
                    <div><label className="text-sm font-medium mb-1 block">Max Price ($)</label><Input type="number" value={editData.price_range_max} onChange={(e) => setEditData({ ...editData, price_range_max: parseInt(e.target.value) || 0 })} /></div>
                  </div>
                  <div><label className="text-sm font-medium mb-1 block">Images (comma-separated URLs)</label><Textarea value={editData.images?.join(', ')} onChange={(e) => setEditData({ ...editData, images: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} rows={2} /></div>
                  <div className="flex gap-2 pt-4"><Button onClick={handleUpdateApplication} className="btn-primary flex-1"><Save className="w-4 h-4 mr-2" />Save</Button><Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button></div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">Business:</span><p className="font-medium">{selectedApp.business_name}</p></div>
                    <div><span className="text-gray-500">Category:</span><p className="font-medium capitalize">{selectedApp.category?.replace('-', ' ')}</p></div>
                    <div><span className="text-gray-500">Locations:</span><p className="font-medium">{selectedApp.cities?.length > 1 ? selectedApp.cities.join(', ') : selectedApp.city}</p></div>
                    <div><span className="text-gray-500">Price:</span><p className="font-medium">${selectedApp.price_range_min} - ${selectedApp.price_range_max}</p></div>
                    <div><span className="text-gray-500">Email:</span><p className="font-medium">{selectedApp.contact_email || '-'}</p></div>
                    <div><span className="text-gray-500">Phone:</span><p className="font-medium">{selectedApp.phone || '-'}</p></div>
                  </div>
                  {selectedApp.special_promotion && (
                    <div className="bg-gold-50 border border-gold-200 rounded-lg p-3 flex items-center gap-2">
                      <Badge className="bg-gold-100 text-gold-800 border border-gold-300">$99 Promo</Badge>
                      <span className="text-sm text-gold-800 font-medium">Special Promotion – $99 for 3 months (vendor opted in)</span>
                    </div>
                  )}
                  <div><span className="text-gray-500 text-sm">Description:</span><p className="mt-1 text-sm bg-gray-50 p-3 rounded-lg">{selectedApp.description}</p></div>
                  {selectedApp.status === 'pending' && (
                    <div className="flex gap-2 pt-4 border-t">
                      <Button onClick={() => openEditMode(selectedApp, 'application')} variant="outline"><Edit className="w-4 h-4 mr-2" />Edit</Button>
                      <Button onClick={() => handleApprove(selectedApp.id)} className="flex-1 bg-green-500 hover:bg-green-600"><Check className="w-4 h-4 mr-2" />Approve</Button>
                      <Button onClick={() => handleReject(selectedApp.id)} variant="destructive"><X className="w-4 h-4 mr-2" />Reject</Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vendor Detail Modal */}
      <Dialog open={!!selectedVendor} onOpenChange={() => setSelectedVendor(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editMode ? 'Edit Vendor' : 'Vendor Details'}</DialogTitle></DialogHeader>
          {selectedVendor && (
            <div className="space-y-4 pt-4">
              {editMode ? (
                <div className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Business Name *</label>
                      <Input value={editData.business_name} onChange={(e) => setEditData({ ...editData, business_name: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Category</label>
                      <Select value={editData.category} onValueChange={(v) => setEditData({ ...editData, category: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{categories.map((cat) => (<SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Phone Number</label>
                      <Input value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} placeholder="(514) 555-0123" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Email</label>
                      <Input type="email" value={editData.contact_email} onChange={(e) => setEditData({ ...editData, contact_email: e.target.value })} placeholder="contact@business.com" />
                    </div>
                  </div>

                  {/* Locations */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Locations Served</label>
                    <div className="border rounded-xl p-3 max-h-40 overflow-y-auto grid grid-cols-3 gap-1" data-testid="admin-vendor-cities">
                      {CITIES.map((c) => (
                        <label key={c} className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={(editData.cities || []).includes(c)}
                            onChange={(e) => {
                              const current = editData.cities || [];
                              const updated = e.target.checked
                                ? [...current, c]
                                : current.filter(x => x !== c);
                              setEditData({ ...editData, cities: updated, city: updated[0] || editData.city });
                            }}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-maroon-600"
                          />
                          {c}
                        </label>
                      ))}
                    </div>
                    {(editData.cities || []).length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">{editData.cities.length} location(s)</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Address</label>
                    <Input value={editData.address} onChange={(e) => setEditData({ ...editData, address: e.target.value })} placeholder="123 Main St, Montreal, QC" />
                  </div>

                  {/* Pricing */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Min Price ($)</label>
                      <Input type="number" value={editData.price_range_min} onChange={(e) => setEditData({ ...editData, price_range_min: parseInt(e.target.value) || 0 })} />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Max Price ($)</label>
                      <Input type="number" value={editData.price_range_max} onChange={(e) => setEditData({ ...editData, price_range_max: parseInt(e.target.value) || 0 })} />
                    </div>
                  </div>

                  {/* Online Presence */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Website</label>
                      <Input value={editData.website} onChange={(e) => setEditData({ ...editData, website: e.target.value })} placeholder="https://www.business.com" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Social Media</label>
                      <Input value={editData.social_media} onChange={(e) => setEditData({ ...editData, social_media: e.target.value })} placeholder="@instagram_handle" />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="text-sm font-medium mb-1 block">Description</label>
                    <Textarea value={editData.description} onChange={(e) => setEditData({ ...editData, description: e.target.value })} rows={3} />
                  </div>

                  {/* Services */}
                  <div>
                    <label className="text-sm font-medium mb-1 block">Services (comma-separated)</label>
                    <Input 
                      value={editData.services?.join(', ')} 
                      onChange={(e) => setEditData({ ...editData, services: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} 
                      placeholder="Wedding Photography, Pre-Wedding Shoots, Albums"
                    />
                  </div>

                  {/* Images Section */}
                  <div className="border-t pt-4">
                    <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                      <Image className="w-4 h-4" /> Vendor Photos ({editData.images?.length || 0} images)
                    </label>
                    
                    {/* Image Preview */}
                    {editData.images?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {editData.images.map((img, i) => (
                          <div key={`edit-img-${i}-${img.slice(-12)}`} className="relative group">
                            <img src={img} alt={`Photo ${i+1}`} className="w-20 h-20 rounded-lg object-cover border" />
                            <button 
                              type="button"
                              onClick={() => setEditData({ ...editData, images: editData.images.filter((_, idx) => idx !== i) })}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add Image URL */}
                    <div className="flex gap-2">
                      <Input 
                        id="new-image-url"
                        placeholder="Paste image URL here and click Add" 
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const url = e.target.value.trim();
                            if (url) {
                              setEditData({ ...editData, images: [...(editData.images || []), url] });
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => {
                          const input = document.getElementById('new-image-url');
                          const url = input.value.trim();
                          if (url) {
                            setEditData({ ...editData, images: [...(editData.images || []), url] });
                            input.value = '';
                          }
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Add multiple image URLs. Press Enter or click Add after each URL.</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button onClick={handleUpdateVendor} className="btn-primary flex-1"><Save className="w-4 h-4 mr-2" />Save Changes</Button>
                    <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Image Gallery */}
                  {selectedVendor.images?.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {selectedVendor.images.map((img, i) => (
                        <img key={`gallery-${i}-${img.slice(-12)}`} src={img} alt={`Photo ${i+1}`} className="w-32 h-24 rounded-lg object-cover flex-shrink-0" />
                      ))}
                    </div>
                  )}
                  
                  {/* Vendor Info Grid */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">Business:</span><p className="font-medium">{selectedVendor.business_name}</p></div>
                    <div><span className="text-gray-500">Category:</span><p className="font-medium capitalize">{selectedVendor.category?.replace('-', ' ')}</p></div>
                    <div><span className="text-gray-500">City:</span><p className="font-medium">{selectedVendor.city}</p></div>
                    <div><span className="text-gray-500">Address:</span><p className="font-medium">{selectedVendor.address || 'N/A'}</p></div>
                    <div><span className="text-gray-500">Phone:</span><p className="font-medium">{selectedVendor.phone || 'N/A'}</p></div>
                    <div><span className="text-gray-500">Email:</span><p className="font-medium">{selectedVendor.email || 'N/A'}</p></div>
                    <div><span className="text-gray-500">Website:</span><p className="font-medium">{selectedVendor.website || 'N/A'}</p></div>
                    <div><span className="text-gray-500">Social:</span><p className="font-medium">{selectedVendor.social_media || 'N/A'}</p></div>
                    <div><span className="text-gray-500">Price Range:</span><p className="font-medium">${selectedVendor.price_range_min?.toLocaleString()} - ${selectedVendor.price_range_max?.toLocaleString()}</p></div>
                    <div><span className="text-gray-500">Featured:</span><p className="font-medium">{selectedVendor.is_featured ? 'Yes' : 'No'}</p></div>
                  </div>
                  
                  {/* Description */}
                  {selectedVendor.description && (
                    <div className="pt-2">
                      <span className="text-gray-500 text-sm">Description:</span>
                      <p className="text-sm mt-1">{selectedVendor.description}</p>
                    </div>
                  )}
                  
                  {/* Services */}
                  {selectedVendor.services?.length > 0 && (
                    <div className="pt-2">
                      <span className="text-gray-500 text-sm">Services:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedVendor.services.map((s, i) => (
                          <Badge key={`svc-${s}-${i}`} variant="secondary" className="text-xs">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Tags Management */}
                  <div className="pt-4 border-t">
                    <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                      <Award className="w-4 h-4" /> Vendor Tags
                    </label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(selectedVendor.tags || []).map((tag) => (
                        <Badge 
                          key={tag} 
                          className={`${tagColors[tag] || 'bg-gray-500'} cursor-pointer`}
                          onClick={() => {
                            const newTags = (selectedVendor.tags || []).filter(t => t !== tag);
                            handleUpdateTags(selectedVendor.id, newTags);
                            setSelectedVendor({ ...selectedVendor, tags: newTags });
                          }}
                        >
                          {tag} <X className="w-3 h-3 ml-1" />
                        </Badge>
                      ))}
                      {(selectedVendor.tags || []).length === 0 && <span className="text-gray-400 text-sm">No tags assigned</span>}
                    </div>
                    <Select 
                      value="" 
                      onValueChange={(tag) => {
                        const currentTags = selectedVendor.tags || [];
                        if (!currentTags.includes(tag)) {
                          handleUpdateTags(selectedVendor.id, [...currentTags, tag]);
                          setSelectedVendor({ ...selectedVendor, tags: [...currentTags, tag] });
                        }
                      }}
                    >
                      <SelectTrigger className="w-48"><SelectValue placeholder="Add a tag..." /></SelectTrigger>
                      <SelectContent>
                        {VENDOR_TAGS.filter(t => !(selectedVendor.tags || []).includes(t)).map((tag) => (
                          <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-4 border-t">
                    <Button variant="outline" onClick={() => openEditMode(selectedVendor, 'vendor')}><Edit className="w-4 h-4 mr-2" />Edit All Details</Button>
                    <Button 
                      variant={selectedVendor.is_featured ? "secondary" : "default"} 
                      onClick={() => { handleToggleFeatured(selectedVendor.id); setSelectedVendor({ ...selectedVendor, is_featured: !selectedVendor.is_featured }); }}
                      className={selectedVendor.is_featured ? '' : 'bg-amber-500 hover:bg-amber-600'}
                    >
                      <Star className={`w-4 h-4 mr-2 ${selectedVendor.is_featured ? 'fill-current' : ''}`} />
                      {selectedVendor.is_featured ? 'Unfeature' : 'Feature'}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => handleMoveToRejected(selectedVendor.id)}
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      data-testid="move-to-rejected-btn"
                    >
                      <X className="w-4 h-4 mr-2" />Move to Rejected
                    </Button>
                    <Button variant="destructive" onClick={() => handleDeleteVendor(selectedVendor.id)}><Trash2 className="w-4 h-4 mr-2" />Delete</Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Blog Form Modal */}
      <Dialog open={showBlogForm} onOpenChange={setShowBlogForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Create Blog Post</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div><label className="text-sm font-medium mb-1 block">Main Image URL</label><Input value={blogForm.image} onChange={(e) => setBlogForm({ ...blogForm, image: e.target.value })} placeholder="https://..." /></div>
            <div><label className="text-sm font-medium mb-1 block">Title</label><Input value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} /></div>
            <div><label className="text-sm font-medium mb-1 block">Category</label>
              <Select value={blogForm.category} onValueChange={(v) => setBlogForm({ ...blogForm, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Planning">Planning</SelectItem>
                  <SelectItem value="Inspiration">Inspiration</SelectItem>
                  <SelectItem value="Traditions">Traditions</SelectItem>
                  <SelectItem value="Tips">Tips</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Excerpt (short description)</label><Textarea value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} rows={2} /></div>
            <div><label className="text-sm font-medium mb-1 block">Content (paragraphs)</label><Textarea value={blogForm.content} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} rows={8} placeholder="Write your blog content here..." /></div>
            <Button onClick={handleCreateBlog} className="w-full btn-primary">Post Blog</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Real Wedding Form Modal */}
      <Dialog open={showWeddingForm} onOpenChange={setShowWeddingForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Add Real Wedding</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div><label className="text-sm font-medium mb-1 block">Title</label><Input value={weddingForm.title} onChange={(e) => setWeddingForm({ ...weddingForm, title: e.target.value })} placeholder="A Beautiful Summer Wedding" /></div>
            <div><label className="text-sm font-medium mb-1 block">Couple Names</label><Input value={weddingForm.couple_names} onChange={(e) => setWeddingForm({ ...weddingForm, couple_names: e.target.value })} placeholder="Priya & Raj" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium mb-1 block">Location</label><Input value={weddingForm.location} onChange={(e) => setWeddingForm({ ...weddingForm, location: e.target.value })} placeholder="Montreal, QC" /></div>
              <div><label className="text-sm font-medium mb-1 block">Wedding Date</label><Input value={weddingForm.wedding_date} onChange={(e) => setWeddingForm({ ...weddingForm, wedding_date: e.target.value })} placeholder="June 2024" /></div>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Description</label><Textarea value={weddingForm.description} onChange={(e) => setWeddingForm({ ...weddingForm, description: e.target.value })} rows={3} /></div>
            <div><label className="text-sm font-medium mb-1 block">Photo URLs (comma-separated)</label><Textarea value={weddingForm.images} onChange={(e) => setWeddingForm({ ...weddingForm, images: e.target.value })} rows={2} placeholder="https://image1.jpg, https://image2.jpg" /></div>
            <Button onClick={handleCreateWedding} className="w-full btn-primary">Add Wedding</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Modal */}
      <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" /> Bulk Upload Vendors
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">CSV Format Instructions</h4>
              <p className="text-sm text-blue-700 mb-2">
                Paste your CSV data below with the following columns (first row must be headers):
              </p>
              <div className="text-xs bg-white p-2 rounded border font-mono overflow-x-auto">
                business_name, category, description, services, price_range_min, price_range_max, city, address, phone, email, website, social_media, images, is_featured, tags
              </div>
              <div className="mt-3 text-xs text-blue-600">
                <strong>Categories:</strong> venues, photographers, makeup, mehndi, decorators, dj, catering, bridal-wear, invitations<br/>
                <strong>Tags:</strong> Best Seller, Customer's Choice, Top Rated, New, Premium, Budget Friendly<br/>
                <strong>Note:</strong> Use comma-separated values for services, images, and tags fields
              </div>
            </div>
            
            <div className="bg-gray-50 border rounded-lg p-3">
              <h4 className="font-medium text-sm mb-2">Example CSV:</h4>
              <pre className="text-xs overflow-x-auto whitespace-pre-wrap font-mono bg-white p-2 rounded border">
{`business_name,category,description,services,price_range_min,price_range_max,city,address,phone,email,website,social_media,images,is_featured,tags
Royal Palace Venue,venues,Elegant wedding venue,Weddings|Receptions|Sangeet,10000,50000,Montreal,123 Main St,(514) 555-0101,info@royal.ca,https://royal.ca,@royalpalace,https://img1.jpg|https://img2.jpg,true,Best Seller|Premium
Dream Photography,photographers,Professional wedding photos,Photography|Videography,2000,8000,Montreal,456 Oak Ave,(514) 555-0202,hello@dream.ca,,@dreamphotos,https://photo1.jpg,false,New`}
              </pre>
              <p className="text-xs text-gray-500 mt-2">* Use pipe (|) separator within cells for multiple values, or standard comma if cell is quoted</p>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Paste CSV Data</label>
              <Textarea 
                value={bulkData} 
                onChange={(e) => setBulkData(e.target.value)} 
                rows={12} 
                placeholder="Paste your CSV data here..."
                className="font-mono text-sm"
              />
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button 
                onClick={handleBulkUpload} 
                disabled={bulkUploading || !bulkData.trim()} 
                className="btn-primary flex-1"
              >
                {bulkUploading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Upload Vendors</>
                )}
              </Button>
              <Button variant="outline" onClick={() => { setShowBulkUpload(false); setBulkData(''); }}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
